import React, { useState, useEffect, useRef } from 'react';
import { MapContainer as LeafletMapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import UserMarkerWrapper from './markers/UserMarkerWrapper';
import LoadingSpinner from '../common/LoadingSpinner';
import { showToast } from '../common/ToastContainer';
import { getAllUsers, User, ConnectionStatus } from '../../services/userService';
import { calculateDistance } from '../../utils/mapping';
import L, { Control } from 'leaflet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { findOrCreateConversation } from '../../services/messageService';

// Filter types
export interface MapFilters {
  professions: {
    [key: string]: boolean;
  };
  experienceLevels: {
    junior: boolean;
    midLevel: boolean;
    senior: boolean;
  };
  interest: string;
  maxDistance: number;
  isDistanceFilterEnabled: boolean;
}

// Props for the map component
interface MapComponentProps {
  filters: MapFilters;
  onSendWave: (userId: number) => void;
  onConnect: (userId: number, userName: string) => void;
  onFilteredUsersChange?: (count: number, sameFieldCount: number) => void;
}

// --- Custom Go Home Control --- //
const GoHomeControl: React.FC = () => {
  const map = useMap();
  const { user: currentUser } = useAuth();
  const defaultZoom = 13;
  const defaultCenter: L.LatLngTuple = [40.7128, -74.0060];

  const handleClick = () => {
    const userCoords: L.LatLngTuple | null = 
        (currentUser?.latitude != null && currentUser?.longitude != null) 
        ? [currentUser.latitude, currentUser.longitude]
        : null;

    if (userCoords) {
      console.log("Centering map on user location:", userCoords);
      map.flyTo(userCoords, defaultZoom);
    } else {
      console.log("User location not available, centering on default.");
      map.flyTo(defaultCenter, 5);
    }
  };

  useEffect(() => {
    const GoHome = L.Control.extend({
      onAdd: function(mapInstance: L.Map) {
        const btn = L.DomUtil.create('button', 'leaflet-bar leaflet-control leaflet-control-custom');
        btn.innerHTML = '<span title="Center on My Location">🏠</span>';
        btn.style.backgroundColor = 'white';
        btn.style.width = '30px';
        btn.style.height = '30px';
        btn.style.fontSize = '1.2rem';
        btn.style.lineHeight = '30px';
        btn.style.textAlign = 'center';
        btn.style.cursor = 'pointer';
        btn.style.border = 'none';
        btn.style.borderRadius = '4px';

        L.DomEvent.disableClickPropagation(btn);
        L.DomEvent.on(btn, 'click', handleClick);
        
        return btn;
      },
    });

    const control = new GoHome({ position: 'topright' });
    control.addTo(map);

    // Cleanup function
    return () => {
       if (map && control) { 
           try {
               map.removeControl(control);
           } catch (error) {
               console.warn("Error removing GoHome control during cleanup:", error);
           }
       }
    };
  }, [map, currentUser, handleClick]);

  return null;
};
// --- End Custom Go Home Control --- //

// --- Component to Reactively Update Map View --- //
interface MapViewUpdaterProps {
  center: L.LatLngTuple;
  zoom: number;
}

const MapViewUpdater: React.FC<MapViewUpdaterProps> = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    console.log("[MapViewUpdater] Updating view to:", center, zoom);
    map.setView(center, zoom);
    // Alternatively, use flyTo for animation: map.flyTo(center, zoom);
  }, [center, zoom, map]); // Depend on center, zoom, and map instance

  return null; // This component doesn't render anything itself
};
// --- End MapViewUpdater --- //

const MapComponent: React.FC<MapComponentProps> = ({ 
  filters, 
  onSendWave, 
  onConnect,
  onFilteredUsersChange
}) => {
  const { user: currentUser, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const homeCoordsRef = useRef<L.LatLngTuple | null>(null);
  const defaultZoom = 13;
  const defaultCenter: L.LatLngTuple = [40.7128, -74.0060];

  // Store user coords in ref when currentUser is available
  useEffect(() => {
    if (currentUser?.latitude != null && currentUser?.longitude != null) {
      homeCoordsRef.current = [currentUser.latitude, currentUser.longitude];
    } else {
      homeCoordsRef.current = null; 
    }
  }, [currentUser]);

  // Calculate initial center once
  const initialCenter: L.LatLngTuple = 
      (currentUser?.latitude != null && currentUser?.longitude != null) 
      ? [currentUser.latitude, currentUser.longitude]
      : defaultCenter;

  const enabledState = !isAuthLoading && isAuthenticated;
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
    refetch: refetchAllUsers
  } = useQuery(
    'allUsers',
    getAllUsers,
    {
      retry: 2,
      refetchOnWindowFocus: false,
      enabled: enabledState, 
    }
  );

  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      refetchAllUsers();
    }
  }, [isAuthLoading, isAuthenticated, refetchAllUsers]);

  useEffect(() => {
    if (usersData && currentUser) {
      let filteredCount = 0;
      let sameFieldCount = 0;
      const currentUserProfession = currentUser.profession?.toLowerCase() || '';

      usersData.forEach(user => {
        if (!user.is_current_user && user.latitude != null && user.longitude != null) {
          const profession = (user.profession || 'other').toLowerCase();
          const professionKey = mapProfessionToKey(profession);
          const passedProfessionFilter = filters.professions[professionKey];

          const experience = user.experience?.toLowerCase() || '';
          let passedExperienceFilter = true;

          if (experience.includes('junior') || experience.includes('jr') || 
              (experience.match(/\d+/) && parseInt(experience.match(/\d+/)![0]) < 4)) {
            passedExperienceFilter = filters.experienceLevels.junior;
          } else if (experience.includes('mid') || experience.includes('mid-level') || 
                  (experience.match(/\d+/) && parseInt(experience.match(/\d+/)![0]) >= 4 && 
                    parseInt(experience.match(/\d+/)![0]) < 8)) {
            passedExperienceFilter = filters.experienceLevels.midLevel;
          } else if (experience.includes('senior') || experience.includes('lead') || 
                    experience.includes('principal') || experience.includes('expert') || 
                    (experience.match(/\d+/) && parseInt(experience.match(/\d+/)![0]) >= 8)) {
            passedExperienceFilter = filters.experienceLevels.senior;
          } else {
            passedExperienceFilter = filters.experienceLevels.midLevel;
          }

          let passedDistanceFilter = true;
          if (filters.isDistanceFilterEnabled && currentUser && currentUser.latitude != null && currentUser.longitude != null) {
            const distance = calculateDistance(
              currentUser.latitude,
              currentUser.longitude,
              user.latitude,
              user.longitude
            );
            passedDistanceFilter = distance <= filters.maxDistance;
          }

          if (passedProfessionFilter && passedExperienceFilter && passedDistanceFilter) {
            filteredCount++;
            
            if (profession.includes(currentUserProfession) || 
                currentUserProfession.includes(profession)) {
              sameFieldCount++;
            }
          }
        }
      });

      if (onFilteredUsersChange) {
        onFilteredUsersChange(filteredCount, sameFieldCount);
      }
    }
  }, [usersData, currentUser, filters, onFilteredUsersChange]);

  const mapProfessionToKey = (profession: string): string => {
    const professionMap: Record<string, string> = {
      'technology': 'technology',
      'tech': 'technology',
      'it': 'technology',
      'software': 'technology',
      'finance': 'finance',
      'financial': 'finance',
      'banking': 'finance',
      'healthcare': 'healthcare',
      'health': 'healthcare',
      'medical': 'healthcare',
      'education': 'education',
      'teaching': 'education',
      'academic': 'education',
      'engineering': 'engineering',
      'engineer': 'engineering',
      'legal': 'legal',
      'law': 'legal',
      'attorney': 'legal',
      'marketing': 'marketing',
      'sales': 'sales',
      'human resources': 'humanResources',
      'hr': 'humanResources',
      'arts': 'artsEntertainment',
      'entertainment': 'artsEntertainment',
      'manufacturing': 'manufacturing',
      'retail': 'retail',
      'government': 'government',
      'energy': 'energy',
      'student': 'student',
      'retired': 'retired'
    };

    for (const [key, value] of Object.entries(professionMap)) {
      if (profession.includes(key)) {
        return value;
      }
    }

    return 'other';
  };

  const getFilteredUsers = () => {
    const validUsers = usersData?.filter(Boolean) || [];
    
    return validUsers.filter(user => {
      if (currentUser && user.id === currentUser.id) {
          return user.latitude != null && user.longitude != null;
      }
      
      if (user.latitude == null || user.longitude == null) return false;

      const profession = (user.profession || 'other').toLowerCase();
      const professionKey = mapProfessionToKey(profession);
      const passedProfessionFilter = filters.professions[professionKey];

      const experience = user.experience?.toLowerCase() || '';
      let passedExperienceFilter = true;

      if (experience.includes('junior') || experience.includes('jr') || 
          (experience.match(/\d+/) && parseInt(experience.match(/\d+/)![0]) < 4)) {
        passedExperienceFilter = filters.experienceLevels.junior;
      } else if (experience.includes('mid') || experience.includes('mid-level') || 
              (experience.match(/\d+/) && parseInt(experience.match(/\d+/)![0]) >= 4 && 
                parseInt(experience.match(/\d+/)![0]) < 8)) {
        passedExperienceFilter = filters.experienceLevels.midLevel;
      } else if (experience.includes('senior') || experience.includes('lead') || 
                experience.includes('principal') || experience.includes('expert') || 
                (experience.match(/\d+/) && parseInt(experience.match(/\d+/)![0]) >= 8)) {
        passedExperienceFilter = filters.experienceLevels.senior;
      } else {
        passedExperienceFilter = filters.experienceLevels.midLevel;
      }

      let passedDistanceFilter = true;
      if (filters.isDistanceFilterEnabled && currentUser && currentUser.latitude != null && currentUser.longitude != null) {
        const distance = calculateDistance(
          currentUser.latitude,
          currentUser.longitude,
          user.latitude,
          user.longitude
        );
        passedDistanceFilter = distance <= filters.maxDistance;
      }

      return passedProfessionFilter && passedExperienceFilter && passedDistanceFilter;
    });
  };

  const handleSendWave = (userId: number) => {
    onSendWave(userId);
    showToast('Wave sent!', 'success');
  };

  const handleInitiateMessage = async (targetUserId: number, targetUserName: string) => {
    if (!currentUser) {
      showToast('Please log in to send messages.', 'danger');
      return;
    }
    
    showToast(`Starting conversation with ${targetUserName}...`, 'info');
    
    try {
      const conversationId = await findOrCreateConversation(targetUserId);
      if (conversationId) {
        console.log('[MapContainer] Invalidating conversations query before navigation...');
        await queryClient.invalidateQueries('conversations');
        navigate('/messages', { state: { conversationId } });
      } else {
        showToast('Could not start conversation. Please try again.', 'danger');
      }
    } catch (error: any) {
      console.error('Error initiating message:', error);
      showToast(error.message || 'Failed to start conversation.', 'danger');
    }
  };

  // <<< Add Logging Here >>>
  useEffect(() => {
    console.log("[MapComponent] Auth State Change Detected:");
    console.log("  - isLoadingAuth:", isAuthLoading);
    console.log("  - isAuthenticated:", isAuthenticated);
    console.log("  - currentUser:", currentUser); // Log the whole user object
    console.log("  - currentUser Lat:", currentUser?.latitude);
    console.log("  - currentUser Lng:", currentUser?.longitude);
  }, [currentUser, isAuthLoading, isAuthenticated]); // Log whenever auth state changes

  // --- Calculate desired center/zoom based on currentUser --- //
  const userHasCoords = currentUser?.latitude != null && currentUser?.longitude != null;
  
  const desiredCenter: L.LatLngTuple = 
      userHasCoords
      ? [currentUser.latitude!, currentUser.longitude!] // Use non-null assertion as we checked
      : defaultCenter; 
  const desiredZoom = userHasCoords ? defaultZoom : 5;

  // <<< Add Logging Here >>>
  console.log("[MapComponent] Calculated View:");
  console.log("  - userHasCoords:", userHasCoords);
  console.log("  - desiredCenter:", desiredCenter);
  console.log("  - desiredZoom:", desiredZoom);

  const filteredUsers = getFilteredUsers();

  if (isAuthLoading || isLoadingUsers) {
     return (
      <LeafletMapContainer
        center={initialCenter}
        zoom={homeCoordsRef.current != null ? defaultZoom : 5}
        scrollWheelZoom={true}
        className="map-container position-relative"
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution="© OpenStreetMap contributors, © CARTO"
        />
        <ZoomControl position="bottomright" />
        <GoHomeControl />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner text="Loading users..." />
        </div>
      </LeafletMapContainer>
    );
  }

  if (usersError) {
    return (
      <div className="alert alert-danger">
        Error loading map data. Please try refreshing the page.
      </div>
    );
  }
  
  return (
    <LeafletMapContainer
      center={initialCenter}
      zoom={homeCoordsRef.current != null ? defaultZoom : 5}
      scrollWheelZoom={true}
      className="map-container"
      zoomControl={false}
    >
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        attribution="© OpenStreetMap contributors, © CARTO"
      />
      
      <ZoomControl position="bottomright" />
      
      <GoHomeControl />
      
      {/* Component to handle reactive view updates */}
      <MapViewUpdater center={desiredCenter} zoom={desiredZoom} />
      
      {filteredUsers.map(user => (
        <UserMarkerWrapper
          key={user.id}
          user={user}
          isCurrentUser={currentUser ? user.id === currentUser.id : false}
          onSendMessage={handleInitiateMessage}
          onSendWave={handleSendWave}
          onConnect={onConnect}
        />
      ))}
    </LeafletMapContainer>
  );
};

export default MapComponent;
